import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { MailingListMemberFormValues } from '@/features/mailing-list'
import type { MailingListSegment } from '@/types'

const SEGMENTS: { value: MailingListSegment; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'film-updates', label: 'Film Updates' },
  { value: 'services', label: 'Services' },
  { value: 'oddfest', label: 'ODDFEST' },
  { value: 'shift', label: 'S.H.I.F.T.' },
]

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (data: MailingListMemberFormValues) => Promise<void>
}

export function AddMemberDialog({ open, onOpenChange, onAdd }: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } =
    useForm<MailingListMemberFormValues>({
      defaultValues: { email: '', firstName: '', lastName: '', segments: ['general'] },
    })

  const selectedSegments = watch('segments') ?? []

  function toggleSegment(value: MailingListSegment) {
    const next = selectedSegments.includes(value)
      ? selectedSegments.filter((s) => s !== value)
      : [...selectedSegments, value]
    setValue('segments', next)
  }

  async function onSubmit(data: MailingListMemberFormValues) {
    setSaving(true)
    setError(null)
    try {
      await onAdd(data)
      reset()
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to add member.'))
    } finally {
      setSaving(false)
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) reset()
    setError(null)
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Member</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="add-email">Email *</Label>
            <Input
              id="add-email"
              type="email"
              placeholder="you@example.com"
              {...register('email', { required: 'Email is required.' })}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{withErrorCode(String(errors.email.message), 'app/validation')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="add-firstName">First Name</Label>
              <Input
                id="add-firstName"
                placeholder="Jane"
                {...register('firstName')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-lastName">Last Name</Label>
              <Input
                id="add-lastName"
                placeholder="Doe"
                {...register('lastName')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Segments</Label>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {SEGMENTS.map(({ value, label }) => (
                <label key={value} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedSegments.includes(value)}
                    onChange={() => toggleSegment(value)}
                    className="h-4 w-4 rounded border-border accent-primary"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Adding…' : 'Add Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
